// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { addDays, calendarMonth, daysInMonth, isoDate, type IsoDate } from "@almanac/domain";
import { weekdayNames } from "@almanac/i18n";
import { feedUrl } from "../lib/api.js";
import { useApp } from "../lib/app.js";
import { moveDate } from "../components/calendarKeys.js";
import { commitments } from "../lib/commitments.js";
import { agenda } from "../lib/events.js";
import { useI18n } from "../lib/i18n.js";
import { CategoryOptions } from "../components/CategoryOptions.js";
import { REPEATS, uuid, type CalendarEvent, type Repeat, type Transaction } from "../lib/model.js";
import { useStore } from "../lib/store.js";

/** How far ahead the "Coming up" list looks. */
const AHEAD_DAYS = 60;

export function Events() {
  const { store, today, announce } = useApp();
  const state = useStore(store);
  const { t, money, date } = useI18n();
  const [selected, setSelected] = useState<IsoDate>(today);
  const [draft, setDraft] = useState<CalendarEvent | null>(null);
  const formRef = useRef<HTMLElement>(null);

  const upcoming = useMemo(() => agenda(state.events, today, addDays(today, AHEAD_DAYS)), [state.events, today]);
  const paid = useMemo(() => {
    const map = new Map<string, Transaction>();
    for (const c of commitments(state.events, state.transactions, today, addDays(today, AHEAD_DAYS))) {
      if (c.paidBy) map.set(`${c.event.id}|${c.date}`, c.paidBy);
    }
    return map;
  }, [state.events, state.transactions, today]);
  const next30 = upcoming.filter((o) => o.date <= addDays(today, 30)).reduce((sum, o) => sum + (o.event.amount ?? 0), 0);

  const startNew = (on: IsoDate) => {
    setDraft({ id: uuid(), title: "", date: on, repeat: "none", until: null, amount: null, category: null, notes: "", hidden: false });
  };

  // Bring the form into view and focus its first field when it opens.
  useEffect(() => {
    if (draft) formRef.current?.querySelector<HTMLElement>("input")?.focus();
  }, [draft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isNew = draft !== null && !state.events.some((e) => e.id === draft.id);

  return (
    <>
      {draft && (
        <section className="card" style={{ marginBottom: 16 }} aria-labelledby="event-form-title" ref={formRef}>
          <h2 id="event-form-title">{isNew ? t("events.add") : t("events.edit")}</h2>
          <EventForm
            key={draft.id}
            initial={draft}
            isNew={isNew}
            onSave={(event) => {
              store.saveEvent(event);
              announce(t("events.saved", { title: event.title }));
              setSelected(event.date);
              setDraft(null);
            }}
            onDelete={() => {
              if (!confirm(t("events.confirmDelete", { title: draft.title }))) return;
              store.deleteEvent(draft.id);
              announce(t("events.deleted", { title: draft.title }));
              setDraft(null);
            }}
            onCancel={() => setDraft(null)}
          />
        </section>
      )}

      <div className="grid events-layout">
        <MonthCard selected={selected} onSelect={setSelected} onAdd={startNew} onEdit={setDraft} />

        <section className="card" aria-labelledby="upcoming-title">
          <h2 id="upcoming-title">{t("events.upcoming")}</h2>
          {state.events.length === 0 ? (
            <p>{t("events.none")}</p>
          ) : upcoming.length === 0 ? (
            <p>{t("events.nothingSoon", { days: AHEAD_DAYS })}</p>
          ) : (
            <>
              {next30 > 0 && <p className="hint">{t("events.next30", { amount: money(next30) })}</p>}
              <ul className="agenda">
                {upcoming.slice(0, 30).map((o) => (
                  <li key={`${o.event.id}|${o.date}`}>
                    <span className="agenda-date">{date(o.date, "short")}</span>
                    <span className="agenda-title">
                      {o.event.title}
                      <span className="hint">{t(`events.repeat.${o.event.repeat}`)}</span>
                    </span>
                    <span className="num">
                      {o.event.amount ? money(o.event.amount) : ""}
                      {paid.has(`${o.event.id}|${o.date}`) && (
                        <span className="hint paid" title={paid.get(`${o.event.id}|${o.date}`)!.description}>
                          {t("events.paid", { date: date(paid.get(`${o.event.id}|${o.date}`)!.date, "short") })}
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => setDraft(o.event)} aria-label={t("events.editNamed", { title: o.event.title })}>
                      {t("events.editShort")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button type="button" className="primary" style={{ marginTop: 12 }} onClick={() => startNew(selected)}>
            {t("events.add")}
          </button>
        </section>
      </div>

      <FeedCard />
    </>
  );
}

function MonthCard({
  selected,
  onSelect,
  onAdd,
  onEdit,
}: {
  selected: IsoDate;
  onSelect: (d: IsoDate) => void;
  onAdd: (d: IsoDate) => void;
  onEdit: (e: CalendarEvent) => void;
}) {
  const { store } = useApp();
  const state = useStore(store);
  const { t, date, locale, money } = useI18n();
  const container = useRef<HTMLTableElement>(null);
  const focusOnChange = useRef(false);
  const instructions = useId();
  const year = Number(selected.slice(0, 4));
  const month = Number(selected.slice(5, 7));
  const first = isoDate(year, month, 1);
  const last = isoDate(year, month, daysInMonth(year, month));

  const byDay = useMemo(() => {
    const map = new Map<IsoDate, CalendarEvent[]>();
    for (const o of agenda(state.events, first, last)) map.set(o.date, [...(map.get(o.date) ?? []), o.event]);
    return map;
  }, [state.events, first, last]);
  const grid = calendarMonth(year, month, () => 0);
  const rows: (typeof grid.cells)[] = Array.from({ length: grid.rows }, () => []);
  for (const cell of grid.cells) rows[cell.row]!.push(cell);
  const onDay = byDay.get(selected) ?? [];

  useEffect(() => {
    if (!focusOnChange.current) return;
    focusOnChange.current = false;
    container.current?.querySelector<HTMLElement>(`[data-date="${selected}"]`)?.focus();
  }, [selected]);

  const go = (key: "PageUp" | "PageDown") => onSelect(moveDate(selected, key)!);

  return (
    <section className="card" aria-labelledby="month-title">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 id="month-title" style={{ margin: 0 }}>
          {date(first, "month")}
        </h2>
        <div className="row">
          <button type="button" onClick={() => go("PageUp")}>
            {t("events.prevMonth")}
          </button>
          <button type="button" onClick={() => go("PageDown")}>
            {t("events.nextMonth")}
          </button>
        </div>
      </div>
      <p className="hint" id={instructions}>
        {t("events.keys")}
      </p>
      <table className="month-grid events-month" role="grid" aria-labelledby="month-title" aria-describedby={instructions} ref={container}>
        <thead>
          <tr>
            {weekdayNames(locale).map((w) => (
              <th scope="col" key={w} abbr={w}>
                {w}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, r) => (
            <tr key={r}>
              {Array.from({ length: 7 }, (_, col) => {
                const cell = cells.find((c) => c.column === col);
                if (!cell) return <td key={col} aria-hidden="true" className="empty" />;
                const count = byDay.get(cell.date)?.length ?? 0;
                const isSelected = cell.date === selected;
                return (
                  <td
                    key={col}
                    role="gridcell"
                    data-date={cell.date}
                    tabIndex={isSelected ? 0 : -1}
                    aria-selected={isSelected}
                    className={count > 0 ? "has-events" : undefined}
                    onClick={() => onSelect(cell.date)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onAdd(cell.date);
                        return;
                      }
                      const next = moveDate(cell.date, e.key);
                      if (next) {
                        e.preventDefault();
                        focusOnChange.current = true;
                        onSelect(next);
                      }
                    }}
                  >
                    <span aria-hidden="true">{Number(cell.date.slice(8, 10))}</span>
                    {count > 0 && (
                      <span className="event-dots" aria-hidden="true">
                        {"•".repeat(Math.min(count, 3))}
                      </span>
                    )}
                    <span className="visually-hidden">
                      {date(cell.date, "long")}, {count === 0 ? t("events.dayNone") : t("events.dayCount", { count })}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginBottom: 6 }}>{date(selected, "long")}</h3>
      {onDay.length === 0 ? (
        <p className="hint">{t("events.dayNone")}</p>
      ) : (
        <ul className="agenda">
          {onDay.map((e) => (
            <li key={e.id}>
              <span className="agenda-title">{e.title}</span>
              <span className="num">{e.amount ? money(e.amount) : ""}</span>
              <button type="button" onClick={() => onEdit(e)} aria-label={t("events.editNamed", { title: e.title })}>
                {t("events.editShort")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" style={{ marginTop: 10 }} onClick={() => onAdd(selected)}>
        {t("events.addOn", { date: date(selected, "short") })}
      </button>
    </section>
  );
}

function EventForm({
  initial,
  isNew,
  onSave,
  onDelete,
  onCancel,
}: {
  initial: CalendarEvent;
  isNew: boolean;
  onSave: (e: CalendarEvent) => void;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const { t, category } = useI18n();
  const [title, setTitle] = useState(initial.title);
  const [day, setDay] = useState(initial.date);
  const [repeat, setRepeat] = useState<Repeat>(initial.repeat);
  const [until, setUntil] = useState(initial.until ?? "");
  const [amount, setAmount] = useState(initial.amount === null ? "" : (initial.amount / 100).toFixed(2));
  const [cat, setCat] = useState(initial.category ?? "");
  const [notes, setNotes] = useState(initial.notes);
  const [hidden, setHidden] = useState(initial.hidden);
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cents = amount.trim() === "" ? null : Math.round(Number(amount.replace(/[$\s]/g, "").replace(",", ".")) * 100);
    if (!title.trim()) return setError(t("events.err.title"));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError(t("events.err.date"));
    if (repeat !== "none" && until && until < day) return setError(t("events.err.until"));
    if (cents !== null && (!Number.isFinite(cents) || cents < 0)) return setError(t("events.err.amount"));
    onSave({
      id: initial.id,
      title: title.trim().slice(0, 200),
      date: day,
      repeat,
      until: repeat !== "none" && until ? until : null,
      amount: cents || null,
      category: cat || null,
      notes,
      hidden,
      ...(initial.purchaseId ? { purchaseId: initial.purchaseId } : {}),
    });
  };

  return (
    <form onSubmit={submit} noValidate>
      {initial.purchaseId && (
        <p className="hint">
          {t("events.fromPurchase")} <a href="#/purchases">{t("nav.purchases")}</a>
        </p>
      )}
      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${id}-title`}>{t("events.title")}</label>
          <input id={`${id}-title`} type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor={`${id}-date`}>{t("events.date")}</label>
          <input id={`${id}-date`} type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor={`${id}-repeat`}>{t("events.repeat")}</label>
          <select id={`${id}-repeat`} value={repeat} onChange={(e) => setRepeat(e.target.value as Repeat)}>
            {REPEATS.map((r) => (
              <option key={r} value={r}>
                {t(`events.repeat.${r}`)}
              </option>
            ))}
          </select>
        </div>
        {repeat !== "none" && (
          <div className="field">
            <label htmlFor={`${id}-until`}>{t("events.until")}</label>
            <input id={`${id}-until`} type="date" value={until} min={day} onChange={(e) => setUntil(e.target.value)} />
          </div>
        )}
        <div className="field">
          <label htmlFor={`${id}-amount`}>{t("events.amount")}</label>
          <input id={`${id}-amount`} type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor={`${id}-category`}>{t("events.category")}</label>
          <select id={`${id}-category`} value={cat} onChange={(e) => setCat(e.target.value)} aria-describedby={`${id}-category-hint`}>
            <option value="">{t("events.noCategory")}</option>
            <CategoryOptions withIncome={false} />
          </select>
          <p className="hint" id={`${id}-category-hint`}>
            {t("events.categoryHint")}
          </p>
        </div>
      </div>
      <div className="field">
        <label htmlFor={`${id}-notes`}>{t("events.notes")}</label>
        <textarea id={`${id}-notes`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <label className="row" style={{ fontWeight: 450, marginBottom: 14 }}>
        <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
        {t("events.hidden")}
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button type="submit" className="primary">
          {t("common.save")}
        </button>
        <button type="button" onClick={onCancel}>
          {t("common.cancel")}
        </button>
        {!isNew && (
          <button type="button" onClick={onDelete} style={{ marginLeft: "auto" }}>
            {t("events.delete")}
          </button>
        )}
      </div>
    </form>
  );
}

function FeedCard() {
  const { store, announce } = useApp();
  const state = useStore(store);
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const url = state.feed.token ? feedUrl(state.feed.token) : null;
  const demo = store.backend.demo;

  const run = async (work: () => Promise<void>, done: string) => {
    setBusy(true);
    try {
      await work();
      announce(done);
    } catch {
      // The store shows the error banner.
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginTop: 16 }} aria-labelledby="feed-title">
      <h2 id="feed-title">{t("feed.title")}</h2>
      <p>{t("feed.intro")}</p>
      <p className="hint">{t("feed.privacy")}</p>
      <fieldset style={{ marginTop: 12 }}>
        <legend>{t("feed.detail")}</legend>
        <div className="choices">
          {(["title", "amount"] as const).map((d) => (
            <label key={d}>
              <input type="radio" name="feed-detail" checked={state.feed.detail === d} onChange={() => store.setFeedDetail(d)} />
              {t(`feed.detail.${d}`)}
            </label>
          ))}
        </div>
      </fieldset>
      {url ? (
        <>
          <div className="field">
            <label htmlFor="feed-url">{t("feed.address")}</label>
            <div className="row">
              <input id="feed-url" type="text" readOnly value={url} style={{ flex: 1 }} onFocus={(e) => e.target.select()} />
              <button
                type="button"
                onClick={() =>
                  void navigator.clipboard?.writeText(url).then(
                    () => announce(t("feed.copied")),
                    () => undefined,
                  )
                }
              >
                {t("feed.copy")}
              </button>
            </div>
          </div>
          <div className="stack" style={{ gap: 8 }}>
            <p className="hint">{t("feed.howTo")}</p>
            <p className="hint">{t("feed.reachable")}</p>
            {demo && <p className="hint">{t("feed.demo")}</p>}
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (confirm(t("feed.confirmNew"))) void run(() => store.startFeed(t("feed.name")), t("feed.renewed"));
              }}
            >
              {t("feed.newAddress")}
            </button>
            <button type="button" disabled={busy} onClick={() => void run(() => store.stopFeed(), t("feed.stopped"))}>
              {t("feed.stop")}
            </button>
          </div>
        </>
      ) : (
        <button type="button" className="primary" disabled={busy} onClick={() => void run(() => store.startFeed(t("feed.name")), t("feed.started"))}>
          {t("feed.start")}
        </button>
      )}
    </section>
  );
}
